import React, { useState } from 'react';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings as SettingsIcon,
  LogOut,
  User,
  ClipboardPenLine,
  Images,
} from 'lucide-react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  useTheme,
  styled,
} from '@mui/material';
import Logo from './Logo';
import TreatmentPlanCTA from './TreatmentPlanCTA';
import { useNavigation } from '../hooks/useNavigation';
import { useUser } from '../hooks/useUser';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { resetCurrentTreatmentPlan, setSentToPatientsIds } from '../redux/slices/treatmentPlan';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: 224, // equivalent to w-56
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: 224,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
}));

const Sidebar = () => {
  const { goTo } = useNavigation();
  const dispatch = useDispatch();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const theme = useTheme();
  const { user, logout } = useUser();
  const currentPage = useLocation().pathname;
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { id: 'patients', label: 'Patients', icon: Users, path: '/dashboard/patients' },
    { id: 'templates', label: 'Saved templates', icon: FileText, path: '/dashboard/templates' },
    { id: 'drafts', label: 'Drafts', icon: ClipboardPenLine, path: '/dashboard/drafts' },
    { id: 'media-library', label: 'Media library', icon: Images, path: '/dashboard/media-library' },
  ];

  const handleNavigate = (path: string) => {
    goTo(path);
  };
  const handleLogout = async () => {
    await logout();
    window.location.reload();
  };
  const handleNewTreatmentPlan = () => {
    dispatch(resetCurrentTreatmentPlan());
    dispatch(setSentToPatientsIds([]));
    handleNavigate('/dashboard/treatment-plan/select-patient');
  };
  return (
    <StyledDrawer variant="permanent" anchor="left">
      <Box
        sx={{
          py: 4,
          pl: 3,
          pr: 2,
          pb: 0,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={{ width: 120, height: 48, display: 'flex', alignItems: 'center' }}>
          <Logo isCompact={false} />
        </Box>
      </Box>

      <TreatmentPlanCTA
        onClick={handleNewTreatmentPlan}
        disabled={currentPage.includes('/treatment-plan')}
        fullWidth
      />

      <List sx={{ py: 3 }}>
        {menuItems.map(item => {
          const Icon = item.icon;
          const isActive = currentPage === item.path;

          return (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                sx={{
                  py: 1.5,
                  px: 3,
                  borderRadius: 0,
                  minHeight: 48,
                  color: isActive ? theme.palette.primary.main : '#555A6A', // muted gray
                  backgroundColor: isActive
                    ? 'rgba(99, 102, 241, 0.08)' // light purple, adjust as needed
                    : 'transparent',
                  borderRight: isActive
                    ? `4px solid ${theme.palette.primary.main}`
                    : '4px solid transparent',
                  fontWeight: isActive ? 500 : 400,
                  '&:hover': {
                    backgroundColor: isActive
                      ? 'rgba(99, 102, 241, 0.08)'
                      : 'rgba(99, 102, 241, 0.04)',
                    color: theme.palette.primary.main,
                  },
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isActive ? theme.palette.primary.main : '#555A6A',
                    transition: 'color 0.2s',
                  }}
                >
                  <Icon style={{ width: 20, height: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    color: 'inherit',
                    fontWeight: isActive ? 500 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ mt: 'auto', p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
          onClick={() => setUserMenuOpen(!userMenuOpen)}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.main,
            }}
          >
            <User style={{ width: 16, height: 16 }} />
          </Avatar>
          <Box sx={{ ml: 2, minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Doctor
            </Typography>
          </Box>
        </Box>

        {userMenuOpen && (
          <Box
            sx={{
              mt: 1,
            }}
          >
            <Box
              onClick={() => handleNavigate('/dashboard/settings')}
              sx={{
                px: 3,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <SettingsIcon style={{ width: 16, height: 16, marginRight: 8 }} />
              <Typography variant="body2">Settings</Typography>
            </Box>
            <Box
              onClick={handleLogout}
              sx={{
                px: 3,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <LogOut style={{ width: 16, height: 16, marginRight: 8 }} />
              <Typography variant="body2">Logout</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </StyledDrawer>
  );
};

export default Sidebar;
